import { createDraftStore } from '../../shared/draft/wizardDraftStore';
import { describeAge, type DraftDescription } from '../../shared/draft/draftDescription';
import type { BuildAudienceData } from '../../context/BuildAudienceContext';

/**
 * The build-audience wizard's draft (`/build-audience`).
 *
 * `BuildAudienceContext` was plain useState with no storage, so a reload reset to step 1
 * with the URL field empty, and a single browser Back left for '/' — which runs
 * resetAll() — and destroyed everything in progress (#475, F13-01/F13-02).
 *
 * `uploadedFile` is deliberately NOT persisted: a File is not JSON, and writing a
 * placeholder for it would restore a file-shaped object with no bytes — the "looks
 * restored and isn't" failure this spec keeps fixing. `uploadedFileContent` (already
 * plain text the app extracted) does persist, so the useful part survives.
 *
 * The STEP is not persisted either, and that is deliberate. App's route effect forces
 * step 101 on every /build-audience mount, so a stored step was written back as 101 on
 * the very next save — a field that always claimed 1 no matter where the user actually
 * was. A draft that lies is worse than one that admits its scope: the inputs are what
 * #475 is about, and re-walking a step is one click. Restoring lands on step 1 with
 * every field already filled.
 */
export type BuildAudienceDraft = Omit<BuildAudienceData, 'uploadedFile'>;

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export const buildAudienceDraftStore = createDraftStore<BuildAudienceDraft>({
  namespace: 'bp.buildAudience.draft.v1',
  storage: 'local',
  maxAgeMs: MAX_AGE_MS,
});

/** Strip the one field that cannot survive JSON, so the caller cannot forget. */
export function toDraft(data: BuildAudienceData): BuildAudienceDraft {
  const rest = { ...data } as Partial<BuildAudienceData>;
  delete rest.uploadedFile;
  return rest as BuildAudienceDraft;
}

/**
 * Competitors the user actually filled in.
 *
 * `initialBuildAudienceData` ships THREE blank `{ name: '', url: '' }` slots, so a bare
 * `.length` is 3 before anything is typed — which made simply opening /build-audience
 * persist a draft and, on return, announce "3 competitors" the user never entered. I saw
 * exactly that string in a browser check and read it as success.
 */
function filledCompetitors(draft: BuildAudienceDraft): number {
  return (draft.userProvidedCompetitors || []).filter((c) => c?.name?.trim() || c?.url?.trim())
    .length;
}

/** Anything the user actually chose or typed. Opening the page is not worth restoring. */
export function buildDraftIsWorthRestoring(
  draft: BuildAudienceDraft | null,
): draft is BuildAudienceDraft {
  if (!draft) return false;
  return Boolean(
    draft.websiteUrl?.trim() ||
      draft.qualitativeInfo?.trim() ||
      draft.specificSegment?.trim() ||
      draft.foundationalResearch?.trim() ||
      draft.businessModel ||
      draft.simulationTarget ||
      draft.audienceName?.trim() ||
      draft.segmentationFocus?.trim() ||
      filledCompetitors(draft) ||
      draft.segments?.length ||
      draft.uploadedFileContent,
  );
}

/** What the restore banner says — built from the draft, never a generic message. */
export function describeBuildDraft(
  draft: BuildAudienceDraft & { updatedAt: number },
  now: number,
): DraftDescription {
  const restored: string[] = [];
  if (draft.websiteUrl?.trim()) restored.push(draft.websiteUrl.trim());
  if (draft.businessModel) restored.push(draft.businessModel);
  if (draft.segmentationFocus?.trim()) restored.push('segmentation focus');
  if (draft.qualitativeInfo?.trim()) restored.push('your notes');
  const competitors = filledCompetitors(draft);
  if (competitors) {
    const n = competitors;
    restored.push(`${n} competitor${n === 1 ? '' : 's'}`);
  }
  if (draft.segments?.length) {
    const n = draft.segments.length;
    restored.push(`${n} segment${n === 1 ? '' : 's'}`);
  }

  return {
    age: describeAge(now - draft.updatedAt),
    restored,
    // An uploaded file cannot survive a reload (see the type docstring), so say so —
    // but only when there actually was one.
    imagesNeedReupload: false,
  };
}
